import { WithModal } from 'hocs/withModal';
import React, { useImperativeHandle } from 'react';
import { Formik, Field, FieldArray } from 'formik';
import {
  Form,
  Segmented,
  Modal,
  Button,
  List,
  Upload as AntdUpload,
  notification,
  Row,
  Col,
  Space,
} from 'antd-v5';
import {
  DatePickerField,
  InfiniteSelectField,
  RadioGroupField,
  SelectField,
  TestItemField,
  TextField,
} from 'components/Molecules/FormFields/index';
import { PlusCircleFilled } from '@ant-design/icons';
import { StaticDataContext } from 'context/StaticDataProvider/index';
import { removeKey } from 'helpers/index';
import classes from './TestData.module.scss';
import T from 'i18n-react';
import * as Yup from 'yup';
import { useList } from 'context/ResourceProvider/hooks/useList';
import get from 'lodash/get';
import { useCreate } from 'context/ResourceProvider/hooks/useCreate';
import isEmpty from 'lodash/isEmpty';

const DEFAULT_VALUE = {
  testData: [
    {
      result: null,
      countryListId: null,
      testItemId: undefined,
      citationId: null,
      reportId: null,
      serviceProviderId: null,
      expiryDate: undefined,
    },
  ],
};

const TestData = React.forwardRef(({ onSubmit, initialValues = DEFAULT_VALUE }, ref) => {
  const { getOptions } = React.useContext(StaticDataContext);
  const formRef = React.useRef();
  const { mutate: createTestItem, loading: createTestItemLoading } = useCreate({
    resource: 'test_items',
  });
  const { mutate: createServiceProvider, loading: createServiceProviderLoading } = useCreate({
    resource: 'compliance-service-providers',
  });

  const handleAction = (listValue, arrayHelpers, meta = {}) => () => {
    const { action, index } = meta;
    switch (action) {
      case 'add': {
        const newTestData = {};
        arrayHelpers.push(newTestData);
        break;
      }
      case 'remove': {
        arrayHelpers.remove(index);
        break;
      }
      case 'duplicate': {
        const duplicateTestData = listValue[index];
        arrayHelpers.push(removeKey(duplicateTestData, 'id'));
        break;
      }
      default: {
        break;
      }
    }
  };

  const handleCreateTestItem = async (newOption, { onChange, onSelect }, countryId) => {
    const response = await createTestItem({
      name: newOption.label,
      countryListId: countryId,
    });
    const createdOption = get(response, 'data.data');
    if (createdOption) {
      onChange(createdOption.id, {
        label: createdOption.name,
        value: createdOption.id,
        data: createdOption,
      });
      onSelect();
    } else {
      notification.error({
        message: 'Create new type failed',
      });
    }
  };

  const handleCreateServiceProvider = async (newOption, { onChange, onSelect }, countryId) => {
    const response = await createServiceProvider({
      name: newOption.label,
      fullName: newOption.label,
    });
    const createdOption = get(response, 'data.data');
    if (createdOption) {
      onChange(createdOption.id, {
        label: createdOption.name,
        value: createdOption.id,
        ...createdOption,
        data: createdOption,
      });
      onSelect();
    } else {
      notification.error({
        message: 'Create new type failed',
      });
    }
  };

  useImperativeHandle(ref, () => ({
    submit: () => {
      return formRef.current.validateForm().then((error) => {
        if (!isEmpty(error)) {
          formRef.current.setTouched(error);
          return false;
        }
        return formRef.current.submitForm();
      });
    },
  }));

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={Yup.object({
        testData: Yup.array().of(
          Yup.object({
            countryListId: Yup.mixed().required(true),
            result: Yup.string().required('The result is required'),
            testItemId: Yup.mixed().required(true),
            serviceProviderId: Yup.mixed().required(true),
          })
        ),
      })}
      onSubmit={onSubmit}>
      {(formikProps) => {
        formRef.current = formikProps;
        const testData = formikProps.values.testData || [];
        return (
          <Form
            layout="vertical"
            requiredMark={(label, { required }) => (
              <>
                {label} {required ? <sup>*</sup> : <sub>*</sub>}
              </>
            )}>
            <FieldArray name="testData">
              {(arrayHelpers) => {
                return (
                  <List
                    dataSource={testData}
                    className={classes.testData}
                    itemLayout="vertical"
                    split={false}
                    footer={
                      <Button
                        type="link"
                        icon={<PlusCircleFilled />}
                        onClick={handleAction(testData, arrayHelpers, {
                          action: 'add',
                        })}>
                        Test Data
                      </Button>
                    }
                    renderItem={(_, index) => {
                      return (
                        <List.Item className={classes.item}>
                          <List.Item.Meta
                            title={
                              <div className={classes.header}>
                                <span className={classes.title}># {index + 1}</span>
                                <Space>
                                  <Button
                                    type="primary"
                                    ghost
                                    onClick={handleAction(testData, arrayHelpers, {
                                      action: 'duplicate',
                                      index,
                                    })}>
                                    Duplicate
                                  </Button>
                                  <Button
                                    type="primary"
                                    ghost
                                    danger
                                    disabled={testData.length <= 1}
                                    onClick={handleAction(testData, arrayHelpers, {
                                      action: 'remove',
                                      index,
                                    })}>
                                    Remove
                                  </Button>
                                </Space>
                              </div>
                            }
                          />
                          <RadioGroupField
                            layoutClasses={classes.testResult}
                            layout="horizontal"
                            label={<h5 className={classes.subtitle}>Result</h5>}
                            name={`testData.[${index}].result`}
                            options={[
                              { value: 'pass', label: 'Passed' },
                              {
                                value: 'fail',
                                label: 'Failed',
                              },
                              { value: 'reportAsIs', label: 'Report As Is' },
                            ]}
                            required
                          />
                          <Row gutter={[16, 16]}>
                            <Col span={12}>
                              <SelectField
                                name={`testData.[${index}].countryListId`}
                                label="Export Market"
                                required
                                options={getOptions('exportMarket')}
                                placeholder={T.translate('general.select')}
                                allowClear
                                onChange={({ field }, _, option) => {
                                  field.onChange({
                                    target: {
                                      name: `testData.[${index}].countryListId`,
                                      value: option,
                                    },
                                  });
                                  // If country was changed. remove the testItemId.
                                  formikProps.setFieldValue(`testData.[${index}].testItemId`, null);
                                  formikProps.setFieldValue(`testData.[${index}].citationId`, null);
                                }}
                              />
                            </Col>
                            <Col span={12}>
                              <TestItemField
                                name={`testData.[${index}].testItemId`}
                                label="Test Item & Citation"
                                countryId={formikProps.values.testData[index].countryListId?.id}
                                placeholder={T.translate('placeholder.selectOrFill')}
                                required
                                disabledWhen={({ form }) => {
                                  const { values } = form;
                                  return !values.testData[index].countryListId;
                                }}
                                creatable
                                loading={createTestItemLoading}
                                onCreate={handleCreateTestItem}
                                onFilterParams={(params) => {
                                  if (params.keywords) {
                                    const { keywords, ...rest } = params;
                                    return {
                                      ...rest,
                                      keyword: keywords,
                                    };
                                  }
                                  return { ...params };
                                }}
                                onChange={({ field, form }, _, option) => {
                                  field.onChange({
                                    target: {
                                      name: `testData.[${index}].testItemId`,
                                      value: option,
                                    },
                                  });
                                  form.setFieldValue(
                                    `testData.[${index}].citationId`,
                                    option.data?.citationList
                                  );
                                }}
                              />
                            </Col>
                          </Row>
                          <Row gutter={[16, 16]}>
                            <Col span={12}>
                              <TextField
                                name={`testData.[${index}].reportId`}
                                placeholder={T.translate('placeholder.fillHere')}
                                label="Report Id"
                              />
                            </Col>
                            <Col span={12}>
                              <InfiniteSelectField
                                resource="compliance-service-providers"
                                name={`testData.[${index}].serviceProviderId`}
                                placeholder={T.translate('placeholder.selectOrFill')}
                                label="Issue Party"
                                labelKey="fullname"
                                filters={{ type: 'common' }}
                                required
                                showArrow
                                creatable
                                onCreate={handleCreateServiceProvider}
                                loading={createServiceProviderLoading}
                                onChange={({ field }, _, option) => {
                                  field.onChange({
                                    target: {
                                      name: `testData.[${index}].serviceProviderId`,
                                      value: option,
                                    },
                                  });
                                }}
                              />
                            </Col>
                          </Row>
                          <Row gutter={[16, 16]}>
                            <Col span={12}>
                              <DatePickerField
                                name={`testData.[${index}].expiryDate`}
                                label="Expiry Date"
                              />
                            </Col>
                            <Col span={12}>
                              <DatePickerField
                                name={`testData.[${index}].testDate`}
                                label="Test Date / Report Date"
                              />
                            </Col>
                          </Row>
                        </List.Item>
                      );
                    }}
                  />
                );
              }}
            </FieldArray>
          </Form>
        );
      }}
    </Formik>
  );
});

export default WithModal(TestData);
